/**
 * What a table IS, as data — the object every screen in the product builds and hands
 * to the engine.
 *
 * ============== WHY IT LEFT `components/TableSetup.tsx` (PHASE 5) ==============
 * It lived in a React component, which was true of the day it was written — that form
 * was the only thing that made one — and has not been true since Phase 2b, when the
 * cards, Custom Table, the seat URL and the Play route all started building `Setup`
 * objects of their own. A type and a default constant in a `.tsx` file drag React into
 * the type graph of everything that touches them, and Phase 5 is where that bit: a
 * plain Node test of `rooms/copy.ts` could not be type-checked, because two links up
 * the import chain sat a component.
 *
 * So the type and its default moved here, to a module with no imports at all, and
 * `tsconfig.tests.json` can include the modules that need them. (`TableSetup.tsx`
 * re-exported both under their old names until it was retired with the classic felt in
 * Phase 9; everything imports them from here now.)
 * ===============================================================================
 */

export interface Setup {
  seats: number;
  heroSeat: number;
  difficulty: number;
  bigBlind: number;
  stackBb: number;
  ante: number;
  format: "cash" | "tournament";
  /** The shuffle seed, as hex. Regenerated for every table unless `seedLocked` is set. */
  seed: string;
  /**
   * The player typed a seed in on purpose, so reuse it.
   *
   * Without this flag the last table's seed persisted in state and sitting down again
   * dealt the identical session — the engine being reproducible is worth a lot, but
   * only when reproducing is something you asked for.
   */
  seedLocked: boolean;
  fastBots: boolean;
  /**
   * Put every seat back to the starting stack before each hand.
   *
   * On by default for cash, because the point of this table is practice at a chosen
   * depth: without it a bot that runs hot is playing 380 big blinds by hand twelve and
   * you are no longer practising the game you sat down to practise. Turn it off to
   * play a session where the stacks actually move.
   */
  resetStacks: boolean;
  /**
   * What a TOURNAMENT seat costs, in Buster Bucks. Ignored at a cash table.
   *
   * Phase 5, box 5. A cash seat's price is arithmetic — a chip is a Buck, so 100 big
   * blinds at 1/2 is 200 — and `seatCost` does it. A tournament's chips are not Bucks,
   * so its price is a separate fact and somebody has to state it. On the eight named
   * cards it is stated on the card; on a Custom table there is no card, and until this
   * field existed a custom tournament was the one seat in the product that cost
   * nothing. That is the seat everybody would have used.
   */
  buyin: number;
}

export const DEFAULT_SETUP: Setup = {
  seats: 9, heroSeat: 0, difficulty: 0.5, bigBlind: 2, stackBb: 100,
  ante: 0, format: "cash",
  // A placeholder. The real seed is minted when a table starts; see seedFor().
  seed: "", seedLocked: false, fastBots: false,
  resetStacks: true,
  /* The Sit & Go's price, which is the tournament somebody building one is copying. */
  buyin: 200,
};
